import React, { useEffect, useState, useRef } from "react";
import { FaSteam, FaGamepad, FaCode, FaRocket, FaExternalLinkAlt, FaMusic, FaChevronLeft, FaChevronRight, FaGlobe, FaShieldAlt, FaRobot, FaInfoCircle } from "react-icons/fa";
import { motion } from 'framer-motion';
import { Link } from "react-router-dom";
import { useInView } from "react-intersection-observer";
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import PageHero from '../components/page/PageHero';
import SectionHeading from '../components/page/SectionHeading';
import ProofStrip from '../components/page/ProofStrip';
import heroImage from '../assets/customDevelopment/hero-workshop.webp';
import blueprintImage from '../assets/customDevelopment/blueprint-plan.webp';
import ctaImage from '../assets/customDevelopment/cta-summit.webp';

// Import Palaxin assets
import libraryHeader from '../assets/palaxin/library_header.png';
import verticalCapsule from '../assets/palaxin/vertical_capsule.png';
import headerCapsule from '../assets/palaxin/header_capsule.png';
// Import Arixa assets
import arixaHome from '../assets/Arixa/Home.png';
// Import The Hangout assets
import hangoutBrowser from '../assets/the_hangout/vdsg02.PNG';

let arixaPlaylistsImg = null;
try {
    // Optional second image for the Arixa slider
    // eslint-disable-next-line global-require
    arixaPlaylistsImg = require('../assets/Arixa/playlists.png');
} catch (e) {
    arixaPlaylistsImg = null;
}

const CustomDevelopment = () => {
    const { t } = useTranslation();
    const [isMobile, setIsMobile] = useState(false);
    const [currentCard, setCurrentCard] = useState(0);
    const [cardWidth, setCardWidth] = useState(0);
    const firstCardRef = useRef(null);
    const totalCards = 3;
    const gapPx = 24; // matches gap-6
    const trackX = -(currentCard * (cardWidth + gapPx));
    const arixaImages = [arixaHome, arixaPlaylistsImg || arixaHome];
    const arrowsTop = isMobile ? '28vh' : '26vh';

    // Mobile detection
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Measure card width for carousel calculations
    useEffect(() => {
        const measure = () => {
            if (firstCardRef.current) {
                setCardWidth(firstCardRef.current.getBoundingClientRect().width);
            }
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, []);

    // Intersection observer for animations. The hero no longer needs one —
    // <PageHero /> renders immediately rather than fading in, so nothing
    // above the fold depends on an observer firing.
    const [portfolioRef, portfolioInView] = useInView({
        triggerOnce: true,
        threshold: isMobile ? 0.05 : 0.2,
        rootMargin: isMobile ? "50px" : "-50px",
    });

    // Fallback mechanism
    const [fallbackVisible, setFallbackVisible] = useState(false);
    useEffect(() => {
        const timer = setTimeout(() => {
            setFallbackVisible(true);
        }, 2000);

        return () => clearTimeout(timer);
    }, []);

    // Pageviews are recorded centrally by <AnalyticsTracker /> in App.js.

    const primaryTextColor = "text-silver-light";
    const secondaryTextColor = "text-silver-medium";
    const customDevelopmentSchema = {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "Service",
                "name": "Custom Web Application Development",
                "serviceType": "Custom software development",
                "provider": {
                    "@type": "Organization",
                    "name": "Riviera Tech Solutions",
                    "url": "https://rivieratech.solutions"
                },
                "areaServed": [
                    { "@type": "Country", "name": "Croatia" }
                ],
                "url": "https://rivieratech.solutions/custom-development",
                "description": "Custom web application development for business workflows, dashboards, booking systems, and custom software."
                // No `offers` block. It carried "minPrice": 499 — one of the
                // stale €199/€499 figures that 2299d833 stripped from the
                // visible copy for being out of date. The number survived here
                // in the JSON-LD, which is precisely the place it does the most
                // damage: search engines read it as our current starting price.
                // Quote per project instead, exactly as /services now does.
            },
            {
                "@type": "FAQPage",
                "mainEntity": [
                    {
                        "@type": "Question",
                        "name": "How much does custom web application development cost?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "MVP custom web applications are quoted by scope, increasing with integrations, user roles, and complexity."
                        }
                    },
                    {
                        "@type": "Question",
                        "name": "Can you build a custom booking system for my business?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Yes. We build custom booking systems with role-based access, payment flows, notifications, and reporting."
                        }
                    },
                    {
                        "@type": "Question",
                        "name": "How do you secure custom business software?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "We design for security from the start: strict validation, least-privilege access, abuse-case review, and safe deployment practices."
                        }
                    }
                ]
            }
        ]
    };

    return (
        <div className="bg-black font-sans min-h-screen">
            <SEO
                title="Custom Web Application Development | Custom Business Software for Growth"
                description="Custom web application developer for business needs: dashboards, portals, booking systems, and custom business software web apps. Built with SEO-aware architecture, secure flows, and happy-customer outcomes for teams in Croatia and beyond."
                keywords="custom web application developer for business, build custom business software web app, web application development, custom software development, developer for custom booking system, hire web developer for ecommerce website, custom web developer Croatia, web developer Zadar"
                url="/custom-development"
                schema={customDevelopmentSchema}
            />
            <PageHero
                eyebrow={t('customDevelopment.hero.eyebrow', 'Custom development')}
                title={t('customDevelopment.hero.title')}
                subtitle={t('customDevelopment.hero.subtitle')}
                ctaLabel={t('customDevelopment.hero.cta', 'Book a free 30-minute consultation')}
                ctaTo="/contact"
                secondaryLabel={t('customDevelopment.hero.secondary', 'See what we have built')}
                secondaryHref="#portfolio"
                proofLine={t('customDevelopment.hero.proof', 'Rated 5.0 on Google · Free consultation · Fixed price agreed before work starts')}
                backgroundImage={heroImage}
            />

            <ProofStrip />

            {/* Portfolio Section */}
            <section
                id="portfolio"
                ref={portfolioRef}
                className="scroll-mt-24 py-16 md:py-24 bg-black"
            >
                <div className="container mx-auto px-4">
                    <div
                        className={`transition-all duration-1000 transform ${(portfolioInView || fallbackVisible)
                            ? "translate-y-0 opacity-100"
                            : "translate-y-10 opacity-0"
                            }`}
                    >
                        <SectionHeading
                            eyebrow={t('customDevelopment.portfolio.eyebrow', 'Shipped work')}
                            title={t('customDevelopment.portfolio.title')}
                            className="mb-16"
                        />

                        {/* Swipeable Cards Carousel */}
                        <div className="relative max-w-6xl mx-auto flex items-start gap-4">
                            {/* Left Arrow */}
                            <button
                                type="button"
                                onClick={() => setCurrentCard((idx) => Math.max(0, idx - 1))}
                                className="sticky text-silver-light hover:text-white transition-colors flex-shrink-0"
                                aria-label="Previous project"
                                style={{ top: arrowsTop, lineHeight: 0 }}
                            >
                                <FaChevronLeft className="w-14 h-14 md:w-16 md:h-16 lg:w-20 lg:h-20 drop-shadow-[0_0_10px_rgba(0,0,0,0.7)] hover:scale-110 transition-transform" />
                            </button>

                            {/* Carousel Track */}
                            <div className="flex-1">
                                <div className="overflow-hidden">
                                    <motion.div
                                        className="flex gap-6"
                                        animate={{ x: trackX }}
                                        transition={{ type: 'spring', stiffness: 280, damping: 32 }}
                                        drag="x"
                                        dragElastic={0.05}
                                        onDragEnd={(e, info) => {
                                            if (info.offset.x < -80 && currentCard < totalCards - 1) {
                                                setCurrentCard(currentCard + 1);
                                            } else if (info.offset.x > 80 && currentCard > 0) {
                                                setCurrentCard(currentCard - 1);
                                            }
                                        }}
                                    >
                                        {/* Arixa Music App Showcase */}
                                        <div ref={firstCardRef} className="w-full flex-shrink-0">
                                            <div className="bg-off-black rounded-lg overflow-hidden shadow-2xl">
                                                {/* App Header */}
                                                <div className="relative">
                                                    <img
                                                        src={arixaImages[0]}
                                                        alt="Arixa Music App Home"
                                                        className="w-full h-auto object-cover"
                                                    />
                                                    <div className="absolute inset-0 bg-black/40"></div>
                                                    <div className="absolute bottom-6 left-6 right-6">
                                                        <div className="flex items-center gap-4 mb-4">
                                                            <div className="flex items-center justify-center w-16 h-16 rounded-lg shadow-lg bg-black/50">
                                                                <FaMusic className={`${primaryTextColor} text-2xl`} />
                                                            </div>
                                                            <div>
                                                                <h3 className={`text-2xl font-bold ${primaryTextColor}`}>{t('customDevelopment.arixa.title')}</h3>
                                                                <p className={`${secondaryTextColor}`}>{t('customDevelopment.arixa.subtitle')}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* App Content */}
                                                <div className="p-8">
                                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                                        {/* App Description */}
                                                        <div>
                                                            <h4 className={`text-xl font-semibold ${primaryTextColor} mb-4`}>{t('customDevelopment.arixa.about')}</h4>
                                                            <p className={`${secondaryTextColor} mb-6 leading-relaxed`}>
                                                                {t('customDevelopment.arixa.description')}
                                                            </p>

                                                            <div className="space-y-4">
                                                                <div className="flex items-center gap-3">
                                                                    <FaCode className={`${primaryTextColor} text-lg`} />
                                                                    <span className={`${secondaryTextColor}`}>{t('customDevelopment.arixa.features.webApp')}</span>
                                                                </div>
                                                                <div className="flex items-center gap-3">
                                                                    <FaRocket className={`${primaryTextColor} text-lg`} />
                                                                    <span className={`${secondaryTextColor}`}>{t('customDevelopment.arixa.features.infrastructure')}</span>
                                                                </div>
                                                                <div className="flex items-center gap-3">
                                                                    <FaMusic className={`${primaryTextColor} text-lg`} />
                                                                    <span className={`${secondaryTextColor}`}>{t('customDevelopment.arixa.features.metadata')}</span>
                                                                </div>
                                                                <div className="flex items-center gap-3">
                                                                    <FaRocket className={`${primaryTextColor} text-lg`} />
                                                                    <span className={`${secondaryTextColor}`}>{t('customDevelopment.arixa.features.aws')}</span>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* App Visual */}
                                                        <div className="flex justify-center">
                                                            <img
                                                                src={arixaPlaylistsImg || arixaHome}
                                                                alt="Arixa Playlists Screenshot"
                                                                className="max-w-full h-auto rounded-lg shadow-lg"
                                                            />
                                                        </div>
                                                    </div>

                                                    {/* App Link */}
                                                    <div className="mt-8 text-center">
                                                        <a
                                                            href="https://app.arixa.io"
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="inline-flex items-center gap-3 bg-moss hover:bg-moss-light text-black px-8 py-4 rounded-md font-medium transition-colors duration-300"
                                                        >
                                                            <FaExternalLinkAlt className="text-xl" />
                                                            <span>{t('customDevelopment.arixa.visitApp')}</span>
                                                        </a>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* The Hangout Browser Showcase */}
                                        <div className="w-full flex-shrink-0">
                                            <div className="bg-off-black rounded-lg overflow-hidden shadow-2xl">
                                                {/* Browser Header – gradient banner (no screenshot) */}
                                                <div className="relative bg-gradient-to-br from-moss-deep via-moss-night to-off-black py-12 px-6">
                                                    <div className="flex items-center gap-4 mb-4">
                                                        <div className="flex items-center justify-center w-16 h-16 rounded-lg shadow-lg bg-black/30">
                                                            <FaGlobe className={`${primaryTextColor} text-2xl`} />
                                                        </div>
                                                        <div>
                                                            <h3 className={`text-2xl font-bold ${primaryTextColor}`}>{t('customDevelopment.theHangout.title')}</h3>
                                                            <p className={`${secondaryTextColor}`}>{t('customDevelopment.theHangout.subtitle')}</p>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Browser Content */}
                                                <div className="p-8">
                                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                                        {/* Browser Description */}
                                                        <div>
                                                            <h4 className={`text-xl font-semibold ${primaryTextColor} mb-4`}>{t('customDevelopment.theHangout.about')}</h4>
                                                            <p className={`${secondaryTextColor} mb-6 leading-relaxed`}>
                                                                {t('customDevelopment.theHangout.description')}
                                                            </p>

                                                            <div className="space-y-4">
                                                                <div className="flex items-center gap-3">
                                                                    <FaShieldAlt className={`${primaryTextColor} text-lg`} />
                                                                    <span className={`${secondaryTextColor}`}>{t('customDevelopment.theHangout.features.adblock')}</span>
                                                                </div>
                                                                <div className="flex items-center gap-3">
                                                                    <FaRobot className={`${primaryTextColor} text-lg`} />
                                                                    <span className={`${secondaryTextColor}`}>{t('customDevelopment.theHangout.features.aiAgent')}</span>
                                                                </div>
                                                                <div className="flex items-center gap-3">
                                                                    <FaGlobe className={`${primaryTextColor} text-lg`} />
                                                                    <span className={`${secondaryTextColor}`}>{t('customDevelopment.theHangout.features.funExploring')}</span>
                                                                </div>
                                                                <div className="flex items-center gap-3">
                                                                    <FaCode className={`${primaryTextColor} text-lg`} />
                                                                    <span className={`${secondaryTextColor}`}>{t('customDevelopment.theHangout.features.localFirst')}</span>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Browser Visual */}
                                                        <div className="flex justify-center">
                                                            <img
                                                                src={hangoutBrowser}
                                                                alt="The Hangout Browser View"
                                                                className="max-w-full h-auto rounded-lg shadow-lg"
                                                            />
                                                        </div>
                                                    </div>

                                                    {/* In Progress Notice */}
                                                    <div className="mt-6 p-4 bg-moss-deep/25 border border-moss/40 rounded-lg">
                                                        <div className="flex items-start gap-3">
                                                            <FaInfoCircle className="text-moss-light text-xl flex-shrink-0 mt-0.5" />
                                                            <div>
                                                                <p className={`${secondaryTextColor} text-sm leading-relaxed`}>
                                                                    <span className="text-moss-light font-semibold">{t('customDevelopment.theHangout.inProgress.title')}</span>{' '}
                                                                    {t('customDevelopment.theHangout.inProgress.message')}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Palaxin Game Showcase */}
                                        <div className="w-full flex-shrink-0">
                                            <div className="bg-off-black rounded-lg overflow-hidden shadow-2xl">
                                                {/* Game Header */}
                                                <div className="relative">
                                                    <img
                                                        src={libraryHeader}
                                                        alt="Palaxin Game Library Header"
                                                        className="w-full h-auto object-cover"
                                                    />
                                                    <div className="absolute inset-0 bg-black/40"></div>
                                                    <div className="absolute bottom-6 left-6 right-6">
                                                        <div className="flex items-center gap-4 mb-4">
                                                            <img
                                                                src={headerCapsule}
                                                                alt="Palaxin Game Logo"
                                                                className="w-16 h-16 rounded-lg shadow-lg"
                                                            />
                                                            <div>
                                                                <h3 className={`text-2xl font-bold ${primaryTextColor}`}>{t('customDevelopment.palaxin.title')}</h3>
                                                                <p className={`${secondaryTextColor}`}>{t('customDevelopment.palaxin.subtitle')}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Game Content */}
                                                <div className="p-8">
                                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                                        {/* Game Description */}
                                                        <div>
                                                            <h4 className={`text-xl font-semibold ${primaryTextColor} mb-4`}>{t('customDevelopment.palaxin.about')}</h4>
                                                            <p className={`${secondaryTextColor} mb-6 leading-relaxed`}>
                                                                {t('customDevelopment.palaxin.description')}
                                                            </p>

                                                            <div className="space-y-4">
                                                                <div className="flex items-center gap-3">
                                                                    <FaGamepad className={`${primaryTextColor} text-lg`} />
                                                                    <span className={`${secondaryTextColor}`}>{t('customDevelopment.palaxin.features.gameplay')}</span>
                                                                </div>
                                                                <div className="flex items-center gap-3">
                                                                    <FaCode className={`${primaryTextColor} text-lg`} />
                                                                    <span className={`${secondaryTextColor}`}>{t('customDevelopment.palaxin.features.procedural')}</span>
                                                                </div>
                                                                <div className="flex items-center gap-3">
                                                                    <FaRocket className={`${primaryTextColor} text-lg`} />
                                                                    <span className={`${secondaryTextColor}`}>{t('customDevelopment.palaxin.features.performance')}</span>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Game Visual */}
                                                        <div className="flex justify-center">
                                                            <img
                                                                src={verticalCapsule}
                                                                alt="Palaxin Game Vertical Capsule"
                                                                className="max-w-full h-auto rounded-lg shadow-lg"
                                                            />
                                                        </div>
                                                    </div>

                                                    {/* Steam Link */}
                                                    <div className="mt-8 text-center">
                                                        <a
                                                            href="https://store.steampowered.com/app/4106650/Palaxin/"
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="inline-flex items-center gap-3 bg-moss hover:bg-moss-light text-off-black px-8 py-4 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg"
                                                        >
                                                            <FaSteam className="text-2xl" />
                                                            <span>{t('customDevelopment.palaxin.playOnSteam')}</span>
                                                            <FaExternalLinkAlt className="text-sm" />
                                                        </a>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                </div>
                                <div className="flex justify-center mt-8">
                                    <div className="flex gap-2">
                                        {[...Array(totalCards)].map((_, idx) => (
                                            <span key={`dot-${idx}`} className={`h-2 w-6 rounded-full ${idx === currentCard ? 'bg-silver-light' : 'bg-silver-dark/60'}`}></span>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Right Arrow */}
                            <button
                                type="button"
                                onClick={() => setCurrentCard((idx) => Math.min(totalCards - 1, idx + 1))}
                                className="sticky text-silver-light hover:text-white transition-colors flex-shrink-0"
                                aria-label="Next project"
                                style={{ top: arrowsTop, lineHeight: 0 }}
                            >
                                <FaChevronRight className="w-14 h-14 md:w-16 md:h-16 lg:w-20 lg:h-20 drop-shadow-[0_0_10px_rgba(0,0,0,0.7)] hover:scale-110 transition-transform" />
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Blueprint band — separates the visual portfolio above from the
                long-form delivery detail below. */}
            <section className="relative overflow-hidden border-y border-moss-deep/40">
                <img
                    src={blueprintImage}
                    alt=""
                    aria-hidden="true"
                    className="h-64 w-full object-cover md:h-80"
                    loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black via-black/75 to-black/30" />
                <div className="absolute inset-0 flex items-center">
                    <div className="container mx-auto px-4">
                        <p className="max-w-xl text-xl md:text-2xl font-serif text-silver-light leading-snug">
                            {t('customDevelopment.blueprint.line', 'Scope agreed in writing, milestones you review before the next one starts.')}
                        </p>
                    </div>
                </div>
            </section>

            <section className="py-16 md:py-20 bg-black">
                <div className="container mx-auto px-4">
                    <div className="max-w-6xl mx-auto">
                        <p className="text-moss-light text-sm font-medium tracking-[0.2em] uppercase mb-4">
                            {t('customDevelopment.deliver.eyebrow', 'How we deliver')}
                        </p>
                        <h2 className={`text-3xl md:text-4xl font-serif font-medium mb-5 ${primaryTextColor}`}>
                            How we deliver custom web application development that keeps customers happy
                        </h2>
                        <p className={`${secondaryTextColor} leading-relaxed max-w-4xl mb-8`}>
                            We follow an audit-driven SEO and delivery process: clear page intent, stronger metadata, conversion-first content,
                            and internal linking that helps Google understand your service architecture. On the product side, we design secure
                            workflows, validate abuse cases early, and ship milestones you can review before scale-up.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="rounded-lg border border-gray-800 bg-off-black p-6">
                                <h3 className={`text-xl font-medium mb-3 ${primaryTextColor}`}>Build for search intent</h3>
                                <p className={`${secondaryTextColor} text-sm leading-relaxed`}>
                                    Each project page targets commercial terms like custom web application development and custom business software,
                                    with clear service detail and direct next steps.
                                </p>
                            </div>
                            <div className="rounded-lg border border-gray-800 bg-off-black p-6">
                                <h3 className={`text-xl font-medium mb-3 ${primaryTextColor}`}>Build for secure growth</h3>
                                <p className={`${secondaryTextColor} text-sm leading-relaxed`}>
                                    We assume hostile traffic, enforce strict validation, and design least-privilege access so your platform stays
                                    stable as usage increases.
                                </p>
                            </div>
                            <div className="rounded-lg border border-gray-800 bg-off-black p-6">
                                <h3 className={`text-xl font-medium mb-3 ${primaryTextColor}`}>Build for happy customers</h3>
                                <p className={`${secondaryTextColor} text-sm leading-relaxed`}>
                                    Faster response times, clearer UX, and predictable delivery milestones reduce friction for your team and your users.
                                </p>
                            </div>
                        </div>
                        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="rounded-lg border border-gray-800 bg-off-black p-6">
                                <h3 className={`text-xl font-medium mb-3 ${primaryTextColor}`}>Who this is for</h3>
                                <p className={`${secondaryTextColor} text-sm leading-relaxed`}>
                                    This service is for teams that have outgrown generic tools and need a custom web application developer for
                                    business workflows. If your team is juggling spreadsheets, disconnected software, or repeated manual
                                    operations, custom software can save time and reduce operational mistakes.
                                </p>
                                <ul className={`${secondaryTextColor} text-sm list-disc pl-5 mt-4 space-y-2`}>
                                    <li>Operations teams needing role-based dashboards and approvals</li>
                                    <li>Service businesses needing a custom booking system and admin panel</li>
                                    <li>Owners wanting a system they fully control and can scale over time</li>
                                </ul>
                            </div>
                            <div className="rounded-lg border border-gray-800 bg-off-black p-6">
                                <h3 className={`text-xl font-medium mb-3 ${primaryTextColor}`}>How we execute projects</h3>
                                <p className={`${secondaryTextColor} text-sm leading-relaxed`}>
                                    We run projects sequentially: discovery, specification, scoped build, QA, and controlled launch. You see
                                    each milestone before the next one starts, so surprises are minimized and decisions stay clear.
                                </p>
                                <ul className={`${secondaryTextColor} text-sm list-disc pl-5 mt-4 space-y-2`}>
                                    <li>Phase 1: requirements and abuse-case mapping</li>
                                    <li>Phase 2: architecture and implementation milestones</li>
                                    <li>Phase 3: launch readiness, logging, and handover</li>
                                </ul>
                            </div>
                        </div>
                        <div className="mt-8 bg-off-black rounded-lg p-6">
                            <h3 className={`text-xl font-medium mb-3 ${primaryTextColor}`}>Timeline, pricing, and ownership</h3>
                            <p className={`${secondaryTextColor} text-sm leading-relaxed`}>
                                A lightweight MVP can be delivered in weeks, while larger systems with deeper workflows or integrations
                                take longer. Pricing is agreed in writing before work begins for
                                early-stage builds and scales with complexity. We define scope up front, separate must-have items from
                                future enhancements, and keep your roadmap practical. The outcome is custom business software that fits your
                                team, your clients, and your growth goals instead of locking you into generic platform constraints.
                            </p>
                        </div>
                        <div className="mt-8 bg-off-black rounded-lg p-6">
                            <h3 className={`text-xl font-medium mb-3 ${primaryTextColor}`}>Security and reliability baseline</h3>
                            <p className={`${secondaryTextColor} text-sm leading-relaxed`}>
                                Every custom build is designed with a "how could this be abused?" mindset. We treat security as a baseline
                                requirement, not an optional add-on. That means validating input paths, reducing privilege levels,
                                protecting key flows, and instrumenting behavior so unusual traffic is easier to diagnose.
                            </p>
                            <ul className={`${secondaryTextColor} text-sm list-disc pl-5 mt-4 space-y-2`}>
                                <li>Input validation and server-side checks on all critical actions</li>
                                <li>Role-based authorization with least-privilege defaults</li>
                                <li>Rate limiting and defensive handling of suspicious request patterns</li>
                                <li>Timestamped logs for flow tracing and incident response</li>
                            </ul>
                        </div>
                        <div className="mt-8 flex flex-wrap gap-4">
                            <Link to="/services" className="text-moss-light hover:text-moss font-medium">
                                Explore all web development services -&gt;
                            </Link>
                            <Link to="/business-websites" className="text-moss-light hover:text-moss font-medium">
                                Need a custom website first? -&gt;
                            </Link>
                            <Link to="/contact" className="text-moss-light hover:text-moss font-medium">
                                Start your custom build -&gt;
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="relative overflow-hidden">
                <img
                    src={ctaImage}
                    alt=""
                    aria-hidden="true"
                    className="h-[26rem] w-full object-cover"
                    loading="lazy"
                />
                <div className="absolute inset-0 bg-black/70" />
                <div className="absolute inset-0 flex items-center">
                    <div className="container mx-auto px-4 text-center">
                        <div className="mx-auto max-w-3xl">
                            <h2 className={`mb-4 text-3xl md:text-4xl font-serif font-medium ${primaryTextColor}`}>
                                {t('customDevelopment.cta.title')}
                            </h2>
                            <p className={`${secondaryTextColor} mb-8`}>
                                {t('customDevelopment.cta.description')}
                            </p>
                            <Link
                                to="/contact"
                                className="inline-block rounded-md bg-moss px-8 py-3 text-lg font-medium text-black transition-colors duration-300 hover:bg-moss-light"
                            >
                                {t('customDevelopment.cta.button')}
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default CustomDevelopment;
